import type { QuoteFailureReason, QuoteOutcome } from './contract.types';

export interface QuoteMint {
  /** Settles exactly once and never rejects. */
  result: Promise<QuoteOutcome>;
  /** Stop retrying and abort anything in flight. */
  cancel(): void;
}

// A 409 means another caller is already minting this session. Waits between
// attempts, unless the response asks for a different one via `Retry-After`.
const RETRY_DELAYS_MS = [150, 300, 600, 1200];

// Bounds the whole operation, retries included, so the app is never left
// waiting on a promise that will not settle. A healthy mint is back in 2 to
// 5 s, slow retries included; past this it is not coming (decided 2026-10-21).
// Half a second inside core's 8 s render watchdog (instance.ts), so the app
// can report the quote's own NETWORK_ERROR before the watchdog's APP_TIMEOUT.
export const DEADLINE_MS = 7_500;

// The edge API version core was built against, sent on the mint so the edge
// answers in the shape core expects even after the API changes. Change it only
// together with code that handles the new version.
export const API_VERSION_HEADER = 'Ekko-Version';
export const API_VERSION = '2026-10-06.pangolin';

interface PriorityRequestInit extends RequestInit {
  priority?: 'high' | 'low' | 'auto';
}

export function quoteUrl(api: string, sessionId: string): string {
  return `${api}/v3/checkout/sessions/${encodeURIComponent(sessionId)}/quote`;
}

export function mintQuote(
  api: string,
  sessionId: string,
  clientSecret: string,
  sessionData: string,
): QuoteMint {
  const url = quoteUrl(api, sessionId);
  const body = JSON.stringify({ sessionData });
  const controller = new AbortController();

  let timedOut = false;
  let cancelled = false;
  let backoff: ReturnType<typeof setTimeout> | undefined;

  const deadline = setTimeout(() => {
    timedOut = true;
    stop();
  }, DEADLINE_MS);

  const result = run().then((outcome) => {
    clearTimeout(deadline);
    return outcome;
  });

  return {
    result,
    cancel() {
      cancelled = true;
      clearTimeout(deadline);
      stop();
    },
  };

  function stop(): void {
    if (backoff !== undefined) clearTimeout(backoff);
    controller.abort();
  }

  async function run(): Promise<QuoteOutcome> {
    mark('ekko:quote-requested');

    for (let attempt = 0; ; attempt++) {
      let response: Response;
      try {
        response = await fetch(url, requestInit());
      } catch {
        return stopped() ?? failed('network');
      }

      if (response.ok) {
        try {
          const quote: unknown = await response.json();
          mark('ekko:quote-fetched');
          return { state: 'ok', quote };
        } catch {
          return stopped() ?? failed('http', response.status);
        }
      }

      if (response.status !== 409 || attempt >= RETRY_DELAYS_MS.length) {
        return failed('http', response.status, await errorCode(response));
      }

      mark('ekko:quote-mint-retry');
      await wait(retryAfterMs(response) ?? RETRY_DELAYS_MS[attempt]);
      const halted = stopped();
      if (halted) return halted;
    }
  }

  function requestInit(): PriorityRequestInit {
    return {
      method: 'POST',
      headers: {
        'X-Client-Secret': clientSecret,
        [API_VERSION_HEADER]: API_VERSION,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
      priority: 'high',
      signal: controller.signal,
    };
  }

  function wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const onAbort = () => {
        if (backoff !== undefined) clearTimeout(backoff);
        backoff = undefined;
        resolve();
      };

      backoff = setTimeout(() => {
        controller.signal.removeEventListener('abort', onAbort);
        backoff = undefined;
        resolve();
      }, ms);

      if (controller.signal.aborted) {
        onAbort();
        return;
      }
      controller.signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  function stopped(): QuoteOutcome | null {
    if (cancelled) return failed('cancelled');
    if (timedOut) return failed('timeout');
    return null;
  }
}

/** The edge's error body is `{ error: "<message>", code: "<CODE>" }`. */
async function errorCode(response: Response): Promise<string | undefined> {
  try {
    const body: unknown = await response.json();
    const code = body !== null && typeof body === 'object' ? (body as Record<string, unknown>).code : undefined;
    return typeof code === 'string' ? code : undefined;
  } catch {
    return undefined;
  }
}

function retryAfterMs(response: Response): number | undefined {
  const seconds = Number(response.headers.get('Retry-After'));
  if (!Number.isFinite(seconds) || seconds <= 0) return undefined;
  return seconds * 1000;
}

function failed(reason: QuoteFailureReason, httpStatus?: number, code?: string): QuoteOutcome {
  const outcome: QuoteOutcome = { state: 'failed', reason };
  if (httpStatus !== undefined) outcome.httpStatus = httpStatus;
  if (code !== undefined) outcome.code = code;
  return outcome;
}

function mark(name: string): void {
  performance.mark(name);
}
